import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { auth } from '../firebase';
import { getFirebaseProjectId } from '../utils/firebaseHelpers';

interface DeploymentStats {
  totalAccounts: number;
  deployedAccounts: number;
  undeployedAccounts: number;
  inProgressAccounts: number;
}

interface EnvironmentDeploymentStats {
  production: DeploymentStats;
  staging: DeploymentStats;
  unclassified: DeploymentStats;
  all: DeploymentStats;
  [key: string]: DeploymentStats; // Add index signature for string keys
}

interface DeploymentStatsResponse {
  success: boolean;
  stats: EnvironmentDeploymentStats;
  timestamp: string;
  environments: {
    production: boolean;
    staging: boolean;
    unclassified: boolean;
  };
  currentEnvironment?: string;
  error?: string;
  message?: string;
}

interface DeploymentResponse {
  success: boolean;
  message: string;
  deployedCount: number;
  failedCount?: number;
  updatedInFirestore?: number;
  environments?: {
    production: number;
    staging: number;
    unclassified: number;
  };
  currentEnvironment?: string;
  error?: string;
  deploymentResults?: Array<{
    accountId: string;
    success: boolean;
    error?: string;
    environment?: string;
  }>;
}

interface UndeploymentResponse {
  success: boolean;
  message: string;
  undeployedCount: number;
  failedCount?: number;
  updatedInFirestore?: number;
  environments?: {
    production: number;
    staging: number;
    unclassified: number;
  };
  currentEnvironment?: string;
  error?: string;
  undeploymentResults?: Array<{
    accountId: string;
    success: boolean;
    error?: string;
    environment?: string;
  }>;
}

const TechnicalAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  
  // Determine the current environment based on project ID and environment variable
  const projectId = getFirebaseProjectId();
  const isStaging = projectId === 'staging-t1fxteam' || process.env.REACT_APP_ENVIRONMENT === 'staging';
  
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deploymentStats, setDeploymentStats] = useState<EnvironmentDeploymentStats>({
    production: {
      totalAccounts: 0,
      deployedAccounts: 0,
      undeployedAccounts: 0,
      inProgressAccounts: 0,
    },
    staging: {
      totalAccounts: 0,
      deployedAccounts: 0,
      undeployedAccounts: 0,
      inProgressAccounts: 0,
    },
    unclassified: {
      totalAccounts: 0,
      deployedAccounts: 0,
      undeployedAccounts: 0,
      inProgressAccounts: 0,
    },
    all: {
      totalAccounts: 0,
      deployedAccounts: 0,
      undeployedAccounts: 0,
      inProgressAccounts: 0,
    }
  });
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [operationDetails, setOperationDetails] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [currentEnvironment, setCurrentEnvironment] = useState<string>(
    isStaging ? 'staging' : 'production'
  );
  
  console.log('Current environment:', currentEnvironment);
  console.log('Project ID:', projectId);
  console.log('Environment variable:', process.env.REACT_APP_ENVIRONMENT);

  // Fetch deployment stats on component mount
  useEffect(() => {
    if (currentUser && userData?.isTechnicalAdmin) {
      fetchDeploymentStats();
    }
  }, [currentUser, userData]);

  // Function to fetch deployment stats
  const fetchDeploymentStats = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Not authenticated");
      }
      
      const token = await user.getIdToken();
      
      // Call the API endpoint exactly like AdminDashboard does
      const functionsUrl = `https://us-central1-${projectId}.cloudfunctions.net`;
      console.log(`Calling getDeploymentStats at: ${functionsUrl}/getDeploymentStats`);
      
      const response = await fetch(
        `${functionsUrl}/getDeploymentStats`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch deployment stats: ${response.status}`);
      }
      
      const data = await response.json() as DeploymentStatsResponse;
      console.log('API Response:', data);
      
      if (!data.success) {
        throw new Error(data.error || data.message || 'Failed to fetch deployment statistics');
      }
      
      // Update the state with the data
      setDeploymentStats(data.stats);
      setLastUpdated(data.timestamp);
      
      // Update current environment if provided by API
      if (data.currentEnvironment) {
        console.log('Setting environment from API:', data.currentEnvironment);
        setCurrentEnvironment(data.currentEnvironment);
      }
      
      console.log('Stats after update:', data.stats);
      
      // Check if current environment has stats
      const envKey = currentEnvironment as keyof EnvironmentDeploymentStats;
      console.log('Current environment stats:', data.stats[envKey]);
    } catch (err) {
      console.error("Error fetching deployment stats:", err);
      setError(err instanceof Error ? err.message : "Unknown error fetching deployment stats");
    } finally {
      setIsLoading(false);
    }
  };

  // Function to deploy all trading accounts
  const handleDeployAllAccounts = async () => {
    if (!window.confirm('Are you sure you want to deploy disconnected trading accounts? This operation cannot be undone and will affect accounts marked as disconnected in the database.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);
      setOperationDetails(null);
      
      console.log('Calling deployAllAccounts function...');
      
      // Get authentication token
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Not authenticated");
      }
      
      const token = await user.getIdToken();
      
      // Call the API endpoint directly like we do for getDeploymentStats
      const functionsUrl = `https://us-central1-${projectId}.cloudfunctions.net`;
      console.log(`Calling deployAllAccounts at: ${functionsUrl}/deployAllAccounts`);
      
      const response = await fetch(
        `${functionsUrl}/deployAllAccounts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to deploy accounts: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json() as DeploymentResponse;
      console.log('Deploy response:', result);
      
      if (result && result.success) {
        setSuccessMessage(result.message || 'Trading accounts have been deployed successfully!');
        
        // Add operation details with environment information
        let details = `Successfully deployed ${result.deployedCount} accounts`;
        if (result.failedCount) {
          details += `, failed to deploy ${result.failedCount} accounts`;
        }
        
        // Add environment breakdown if available
        if (result.environments) {
          const env = result.environments;
          details += `. Environment breakdown: Production: ${env.production}, Staging: ${env.staging}, Unclassified: ${env.unclassified}`;
        }
        
        // Add database update information if available
        if (result.updatedInFirestore !== undefined) {
          details += `. Updated ${result.updatedInFirestore} database records with isConnected=true.`;
        }
        
        setOperationDetails(details);
        
        // Refresh the stats
        fetchDeploymentStats();
      } else {
        // Handle non-success response
        const errorMessage = result.error || result.message || 'Failed to deploy accounts';
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      console.error('Error deploying accounts:', err);
      
      // Extract error message
      let errorMessage = 'Failed to deploy accounts. Please try again later.';
      
      if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to undeploy all trading accounts
  const handleUndeployAllAccounts = async () => {
    if (!window.confirm('Are you sure you want to undeploy connected trading accounts? This will stop automated trading for accounts marked as connected in the database.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);
      setOperationDetails(null);
      
      console.log('Calling undeployAllAccounts function...');
      
      // Get authentication token
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Not authenticated");
      }
      
      const token = await user.getIdToken();
      
      // Call the API endpoint directly like we do for getDeploymentStats
      const functionsUrl = `https://us-central1-${projectId}.cloudfunctions.net`;
      console.log(`Calling undeployAllAccounts at: ${functionsUrl}/undeployAllAccounts`);
      
      const response = await fetch(
        `${functionsUrl}/undeployAllAccounts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to undeploy accounts: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json() as UndeploymentResponse;
      console.log('Undeploy response:', result);
      
      if (result && result.success) {
        setSuccessMessage(result.message || 'Trading accounts have been undeployed successfully!');
        
        // Add operation details with environment information
        let details = `Successfully undeployed ${result.undeployedCount} accounts`;
        if (result.failedCount) {
          details += `, failed to undeploy ${result.failedCount} accounts`;
        }
        
        // Add environment breakdown if available
        if (result.environments) {
          const env = result.environments;
          details += `. Environment breakdown: Production: ${env.production}, Staging: ${env.staging}, Unclassified: ${env.unclassified}`;
        }
        
        // Add database update information if available
        if (result.updatedInFirestore !== undefined) {
          details += `. Updated ${result.updatedInFirestore} database records with isConnected=false.`;
        }
        
        setOperationDetails(details);
        
        // Refresh the stats
        fetchDeploymentStats();
      } else {
        // Handle non-success response
        const errorMessage = result.error || result.message || 'Failed to undeploy accounts';
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      console.error('Error undeploying accounts:', err);
      
      // Extract error message
      let errorMessage = 'Failed to undeploy accounts. Please try again later.';
      
      if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Get the stats for the current environment
  const currentStats = deploymentStats[currentEnvironment as keyof EnvironmentDeploymentStats] || {
    totalAccounts: 0,
    deployedAccounts: 0,
    undeployedAccounts: 0,
    inProgressAccounts: 0
  };
  
  console.log('Current stats at render time:', currentStats);
  console.log('All deployment stats at render time:', deploymentStats);

  // Only technical admins can access this page
  if (!currentUser || !userData) {
    return <Navigate to="/" />;
  }

  if (!userData.isTechnicalAdmin) {
    return <Navigate to="/admin" />;
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-12">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Technical Admin Dashboard</h1>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Back to Admin Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Deployment Stats Card */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">
                Deployment Statistics 
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({currentEnvironment === 'production' ? 'Production' : 'Staging'} Environment)
                </span>
              </h2>
              {lastUpdated && (
                <p className="text-xs text-gray-500">
                  Last updated: {new Date(lastUpdated).toLocaleString()}
                </p>
              )}
            </div>
            {isLoading && !error ? (
              <div className="mt-4 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-t1blue"></div>
              </div>
            ) : error ? (
              <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {/* Total Accounts */}
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Accounts</dt>
                    <dd className="mt-1 text-3xl font-semibold text-gray-900">{currentStats.totalAccounts}</dd>
                  </div>
                </div>

                {/* Deployed Accounts */}
                <div className="bg-green-50 overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <dt className="text-sm font-medium text-green-800 truncate">Deployed Accounts</dt>
                    <dd className="mt-1 text-3xl font-semibold text-green-900">{currentStats.deployedAccounts}</dd>
                  </div>
                </div>

                {/* Undeployed Accounts */}
                <div className="bg-yellow-50 overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <dt className="text-sm font-medium text-yellow-800 truncate">Undeployed Accounts</dt>
                    <dd className="mt-1 text-3xl font-semibold text-yellow-900">{currentStats.undeployedAccounts}</dd>
                  </div>
                </div>

                {/* In Progress Accounts */}
                <div className="bg-blue-50 overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <dt className="text-sm font-medium text-blue-800 truncate">In Progress</dt>
                    <dd className="mt-1 text-3xl font-semibold text-blue-900">{currentStats.inProgressAccounts}</dd>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons Card */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-medium text-gray-900">Deployment Actions</h2>
            <p className="mt-1 text-sm text-gray-500">
              These actions will affect trading accounts based on their current connection status in the database.
            </p>
            
            {successMessage && (
              <div className="mt-4 bg-green-50 border-l-4 border-green-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">{successMessage}</p>
                    {operationDetails && (
                      <p className="text-sm text-green-600 mt-1">{operationDetails}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-5 space-y-4 sm:space-y-0 sm:space-x-4 sm:flex">
              <button
                onClick={handleDeployAllAccounts}
                disabled={isLoading}
                className="w-full sm:w-auto flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Deploys accounts that are marked as isConnected=false in the database and are in a deployable state in MetaAPI"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>Deploy Disconnected Accounts</>
                )}
              </button>
              <button
                onClick={handleUndeployAllAccounts}
                disabled={isLoading}
                className="w-full sm:w-auto flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Undeploys accounts that are marked as isConnected=true in the database and are currently deployed in MetaAPI"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>Undeploy Connected Accounts</>
                )}
              </button>
              <button
                onClick={fetchDeploymentStats}
                disabled={isLoading}
                className="w-full sm:w-auto flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Refreshing...
                  </>
                ) : (
                  <>Refresh Stats</>
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Technical Note */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-md font-medium text-gray-900 mb-2">Technical Note</h3>
          <p className="text-sm text-gray-600">
            This dashboard uses three Firebase Cloud Functions:
          </p>
          <ul className="mt-2 list-disc list-inside text-sm text-gray-600 space-y-1">
            <li><code>getDeploymentStats</code> - Returns statistics about deployed/undeployed accounts by environment</li>
            <li><code>deployAllAccounts</code> - Deploys accounts that are marked as <code>isConnected=false</code> in the database</li>
            <li><code>undeployAllAccounts</code> - Undeploys accounts that are marked as <code>isConnected=true</code> in the database</li>
          </ul>
          <p className="mt-2 text-sm text-gray-600">
            The dashboard synchronizes account connection status between the database and MetaAPI. When accounts are deployed, their <code>isConnected</code> field is set to <code>true</code>. When accounts are undeployed, their <code>isConnected</code> field is set to <code>false</code>.
          </p>
          <p className="mt-2 text-sm text-gray-600">
            Environment classification is based on server names and metadata from the database. 
            The dashboard shows statistics for the {currentEnvironment} environment ({projectId}).
          </p>
        </div>
      </main>
    </div>
  );
};

export default TechnicalAdminDashboard; 